<template>
    <AonCard class="concept-patent-filing-chart-card display-flex flex-column w-100 mb-sm">
        <p>Patent Filing Trends by Product Area</p>
        <AonSkeleton
            class="w-100 display-flex align-items-center mb-sm flex-wrap"
            :loading="dataLoading"
            :data="0"
            :height="'100px'"
            light
        >
            <div class="concept-patent-filing-chart-holder w-100">
                <AonAlertBox
                    v-if="dataError"
                    is-contextual
                    :title="dataError"
                    type="notification"
                />
                <div
                    v-else
                    id="conceptPatentFilingMapChart"
                    :style="{ height: chartHeight + 'px' }"
                />
            </div>
        </AonSkeleton>
    </AonCard>
</template>

<script setup>
import { useProspectivePatentPlaybookStore } from '@/stores/prospectivePatentPlaybook.js'
import { computed, nextTick, ref, watch } from 'vue'
import * as am5 from '@amcharts/amcharts5'
import { config } from '@/config.js'
import { useViolinChart } from '@/composables/charts/violinChart.js'
import { logger } from '@moatmetrics/vue-logger'
import AonAlertBox from '@moatmetrics/armory/src/components/base/AonAlertBox.vue'

const playbookStore = useProspectivePatentPlaybookStore()

const chartData = ref(null)
let root = null

const dataLoading = computed(
    () => playbookStore.loadingStates?.conceptPatentFilingYears?.loading ?? true
)
const dataError = computed(() => playbookStore.loadingStates?.conceptPatentFilingYears?.error)

const conceptPatentFilingYears = computed(() => playbookStore.conceptPatentFilingYears ?? [])

const chartHeight = computed(() => {
    const baseHeight = 150
    const heightPerProduct = 50
    const calculatedHeight = baseHeight + conceptPatentFilingYears.value.length * heightPerProduct

    return Math.max(calculatedHeight, 150)
})

const mapDataToChart = () => {
    if (conceptPatentFilingYears.value.length <= 0) return

    chartData.value = conceptPatentFilingYears.value.flatMap((concept) => {
        // Map each year's filing data to the format expected by the violin chart
        return concept.yearCounts.map((metric) => ({
            filingYear: metric.year,
            powerScore: metric.count,
            displayName: concept.name,
        }))
    })

    const violinRow = conceptPatentFilingYears.value.map((concept) => ({
        displayName: concept.name,
        color: '#1F589A',
    }))

    nextTick(() => {
        initializeChart(violinRow)
    })
}

const initializeChart = async (violinRow) => {
    if (root) {
        root.dispose()
        root = null
    }

    await nextTick()

    // Only initialize chart if the component is still mounted and we have data
    if (!chartData.value || chartData.value.length === 0) return

    try {
        am5.addLicense(config.license.AMChartsLicense)
        root = am5.Root.new('conceptPatentFilingMapChart')

        const violinChart = useViolinChart(root, violinRow, chartData.value)

        violinChart.configureViolin({ scrollContainer: true, snapToTicks: false })
        violinChart.createAllSeries({
            truncate: true,
            labelLength: 30,
            scoreLabel: 'Filing Count',
            targetItem: null,
            snapToTicks: false,
        })
    } catch (err) {
        logger.error('Error initializing chart:', err)
    }
}

watch(
    () => conceptPatentFilingYears.value,
    (newVal, oldVal) => {
        if (newVal.length > 0) {
            mapDataToChart()
        }
    },
    { immediate: true }
)
</script>

<style scoped lang="scss">
.concept-patent-filing-chart-card {
    gap: 10px;
}
</style>
